import firebase from "firebase/app";
import "firebase/functions";

import { asDate } from "src/utils/date";
import { formatDate } from "../../utils";
import { getUserId } from "../../config/storage";

import type { Inventory } from "src/interfaces/inventory.interface";
import type { User } from "src/interfaces/systemState.interface";
import type { Sale } from "src/interfaces/sale.interface";
import chunk from "lodash/chunk";
import { MaximumBatchSize } from "src/config/firestore";
import firestore from "src/apiService/firestore";
import storage from "src/apiService/storage";
import { stampNormalizedMatchFields } from "src/utils/normalizeMatchKey";
import {
  calculateGrossProfit,
  computeMinSaleDate,
  computeMaxSaleDate,
  daysOnPlatform,
  calculateNetProfit,
} from "src/utils/sales";

import convertValue from "src/utils/convertValue";
import { getEffectiveTimeZoneFromUser } from "src/utils/timezone";
import parallelize from "src/utils/parallelize";

const db = firestore();

export const fetchSalesFirebase = async (payload: any) => {
  const userId = await getUserId();
  const results: any[] = [];
  const snapshot = await db
    .collection("Sales")
    .where("user", "==", userId)
    .get();
  snapshot.forEach((doc) => results.push(doc.data()));
  return { data: results, length: snapshot.size, total: snapshot.size };
};

export async function uploadSalesApi(basename: string, filename: string) {
  const f = firebase.functions().httpsCallable("api-postSales");
  return (await f({ basename, filename })).data;
}

function payloadToSale(
  userId: string,
  docId: string,
  payload: any,
  income_tax_rate: number
) {
  const data = {
    ...payload,
    purchase_date: convertValue(payload.purchase_date, "date"),
    list_date: convertValue(payload.list_date, "date"),
    purchase_price: convertValue(payload.purchase_price, "numeric") || 0,
    sale_date: convertValue(payload.sale_date, "date"),
    return_date: convertValue(payload.return_date, "date"),
    sale_price: convertValue(payload.sale_price, "numeric") || 0,
    shipping_cost: convertValue(payload.shipping_cost, "numeric") || 0,
    transaction_fees: convertValue(payload.transaction_fees, "numeric") || 0,
    shipping_cost_analytics:
      convertValue(payload.shipping_cost_analytics, "numeric") || 0,
    other_fees: convertValue(payload.other_fees, "numeric") || 0,
    extra_shipping_cost:
      convertValue(payload.extra_shipping_cost, "numeric") || 0,
    extra_transaction_fees:
      convertValue(payload.extra_transaction_fees, "numeric") || 0,
    sales_tax: convertValue(payload.sales_tax, "numeric") || 0,
    liable_to_pay: convertValue(payload.liable_to_pay, "boolean"),
    brand: payload.brand || "",
    category: payload.category || "",
    sub_category: payload.sub_category || "",
    department: payload.department || "",
    item_option: null,
    days_on_platform: 0,
    item_title: payload.item_title || "",
    location: payload.location || "",
    net_profit: 0,
    notes: payload.notes || "",
    other_business_costs: 0,
    gross_profit: 0,
    sale_tax_paid: null,
    user: userId,
    is_return: payload.is_return || false,
    vendor: payload.vendor || "",
    id: docId,
  };

  for (const key of Object.keys(data)) {
    if (data[key] === undefined) delete data[key];
  }

  data.days_on_platform = daysOnPlatform(data.sale_date, data.list_date);

  // Calculate gross profit
  data.gross_profit = calculateGrossProfit(data);

  // Calculate estimates income tax
  data.estimated_income_tax = data.gross_profit * income_tax_rate;

  // Calculate net profit from calculated gross profit and estimates income tax
  data.net_profit = calculateNetProfit(data);

  data.min_date = computeMinSaleDate(data);
  data.max_date = computeMaxSaleDate(data);

  return data;
}

export async function updateSaleApi(payload: any) {
  if (!payload.id) return;

  const userId = await getUserId();
  return await db.runTransaction(async (transaction) => {
    const userData = await transaction.get(db.collection("Users").doc(userId));
    const income_tax_rate = userData.data()?.income_tax_rate || 0;
    const ref = db.collection("Sales").doc(payload.id);
    const doc = await transaction.get(ref);

    const existing = doc.data() || {};
    const data = payloadToSale(
      userId,
      payload.id,
      {
        ...existing,
        ...payload,
      },
      income_tax_rate
    );

    const existingSaleDate = asDate(existing.sale_date);
    const newSaleDate = asDate(data.sale_date);
    const clearPostedDate =
      !!asDate(existing.posted_date) &&
      !!existingSaleDate &&
      !!newSaleDate &&
      existingSaleDate.getTime() !== newSaleDate.getTime();

    await transaction.set(
      ref,
      {
        ...data,
        ...(clearPostedDate
          ? { posted_date: firebase.firestore.FieldValue.delete() }
          : {}),
        etsy_receipt_allocated_error: firebase.firestore.FieldValue.delete(),
        etsy_refund_allocated_error: firebase.firestore.FieldValue.delete(),
      },
      { merge: true }
    );

    return {
      success: true,
      statusCode: 200,
      message: "Sale has been updated successfully",
    };
  });
}

interface MoveSaleApiOptions {
  allowZeroCostDelete?: boolean;
}

export async function moveSaleApi(
  payload: Sale,
  options: MoveSaleApiOptions = {}
) {
  const saleId = payload?.id;
  if (!saleId) return;

  const user = await getUserId();
  const keys = [
    "brand",
    "category",
    "department",
    "item_option",
    "item_title",
    "list_date",
    "location",
    "notes",
    "platforms_listed",
    "purchase_date",
    "purchase_price",
    "sku",
    "sub_category",
    "vendor",
    "bank_account",
  ];
  const data: any = { user };

  for (const k of keys) {
    if (k in payload && payload[k] !== undefined) data[k] = payload[k];
  }

  return await db.runTransaction(async (t) => {
    const saleDoc = db.collection("Sales").doc(saleId);
    const sale = await t.get(saleDoc);
    if (!sale.exists)
      throw new Error(`moveSaleApi: Sale (#${saleId}) doesn't exist`);

    const saleData = sale.data() || {};
    const transactionInventoryId = saleData.transaction_inventory_id;
    const purchasePrice = Number(
      saleData.purchase_price ?? payload.purchase_price ?? 0
    );
    const hasPurchasePrice =
      Number.isFinite(purchasePrice) && purchasePrice !== 0;

    // Optional mode for cancellation sync flows:
    // If the sale has no linked inventory transaction and no cost basis,
    // delete it instead of creating a zero-cost inventory record.
    if (
      options.allowZeroCostDelete &&
      !transactionInventoryId &&
      !hasPurchasePrice
    ) {
      await t.delete(saleDoc);
      return saleId;
    }

    const doc = db.collection("Inventory").doc();

    if (transactionInventoryId) {
      const transactionDoc = db
        .collection("Transactions")
        .doc(transactionInventoryId);
      try {
        const transaction = await t.get(transactionDoc);
        if (transaction.exists) {
          const transactionData = transaction.data();
          const items = (transactionData?.items || []).filter(
            (i) => i.type !== "sale" || i.id !== saleId
          );
          items.push({ type: "inventory", id: doc.id });
          data.transaction_inventory_id = transactionInventoryId;
          await t.update(transactionDoc, {
            items,
          });
        }
      } catch (e) {
        console.warn(
          "[sales] moveSaleApi: failed to update linked transaction; continuing move",
          { saleId, transactionInventoryId, error: e }
        );
      }
    }

    await t.set(
      doc,
      stampNormalizedMatchFields({
        ...data,
        id: doc.id,
      })
    );
    await t.delete(saleDoc);
    return doc.id;
  });
}

export const moveBundledSalesApi = async (payload: any) => {
  const userId = await getUserId();
  const userSnap = await db.collection("Users").doc(userId).get();
  const timeZone = getEffectiveTimeZoneFromUser(
    userSnap.data() as Pick<User, "timezone"> | undefined
  );
  const f = firebase.functions().httpsCallable("moveBundledSalesToInventory");
  const { data } = await f({
    items: payload.map((item: any) => ({
      ...item,
      list_date: item.list_date
        ? formatDate(item.list_date, "YYYY-MM-DD", timeZone)
        : undefined,
      purchase_date: item.purchase_date
        ? formatDate(item.purchase_date, "YYYY-MM-DD", timeZone)
        : undefined,
      sale_date: item.sale_date
        ? formatDate(item.sale_date, "YYYY-MM-DD", timeZone)
        : undefined,
    })),
  });
  return data;
};

export async function addSaleApi(payload: any) {
  const userId = await getUserId();
  return await db.runTransaction(async (transaction) => {
    const userData = await transaction.get(db.collection("Users").doc(userId));
    const income_tax_rate = userData.exists
      ? userData.data()?.income_tax_rate || 0
      : 0;
    const doc = db.collection("Sales").doc();
    const data = payloadToSale(userId, doc.id, payload, income_tax_rate);
    await transaction.set(doc, data);

    return {
      success: true,
      statusCode: 201,
      message: `${doc.id} has been added successfully`,
      id: doc.id,
    };
  });
}

export async function addSaleFromInventory(
  inventoryId: string,
  payload: Omit<Sale, "id">
) {
  const userId = await getUserId();

  return await db.runTransaction(async (t) => {
    const userData = await t.get(db.collection("Users").doc(userId));
    const income_tax_rate = userData.exists
      ? userData.data()?.income_tax_rate || 0
      : 0;
    const doc = db.collection("Sales").doc();
    const data = {
      ...payload,
      sale_price: payload.sale_price || 0,
      purchase_price: payload.purchase_price || 0,
      shipping_cost: payload.shipping_cost || 0,
      transaction_fees: payload.transaction_fees || 0,
      extra_shipping_cost: payload.extra_shipping_cost || 0,
      extra_transaction_fees: payload.extra_transaction_fees || 0,
      gross_profit: 0,
      net_profit: 0,
      item_option: null,
      other_business_costs: 0,
      sale_tax_paid: null,
      user: userId,
      is_return: payload.is_return || false,
      vendor: payload.vendor || "",
      days_on_platform: daysOnPlatform(payload.sale_date, payload.list_date),
      id: doc.id,
    };

    // Calculate gross profit
    data.gross_profit = calculateGrossProfit(data);

    // Calculate estimates income tax
    data.estimated_income_tax = data.gross_profit * income_tax_rate;

    // Calculate net profit from calculated gross profit and estimates income tax
    data.net_profit =
      data.gross_profit - data.estimated_income_tax - data.other_business_costs;
    data.min_date = computeMinSaleDate(data);
    data.max_date = computeMaxSaleDate(data);

    const inventoryDoc = db.collection("Inventory").doc(inventoryId);
    const inventory = await t.get(inventoryDoc);
    if (inventory.exists) {
      const inventoryData = inventory.data();
      const quantity = inventoryData?.quantity || 1;
      if (inventoryData?.transaction_inventory_id) {
        data.transaction_inventory_id = inventoryData.transaction_inventory_id;
        const transactionDoc = db
          .collection("Transactions")
          .doc(inventoryData.transaction_inventory_id);
        const transaction = await t.get(transactionDoc);
        if (transaction.exists) {
          const transactionData = transaction.data();
          let items = transactionData?.items || [];
          items.push({ type: "sale", id: doc.id });
          if (quantity <= 1)
            items = items.filter(
              (it) => it.type !== "inventory" || it.id !== inventoryId
            );
          await t.update(transactionDoc, {
            items,
          });
        }
      }

      if (quantity > 1) {
        await t.update(inventoryDoc, {
          quantity: quantity - 1,
          purchase_price:
            (inventoryData?.purchase_price || 0) -
            (payload.purchase_price || 0),
        });
      } else {
        await t.delete(inventoryDoc);
      }
    }

    await t.set(doc, data);
    return doc.id;
  });
}

export const updateBundledSalesApi = async (payload: any) => {
  const f = firebase.functions().httpsCallable("bundledSalesUpdate");
  return (await f({ items: payload })).data;
};

export const deleteBundledSalesApi = async (payload: { id: string }[]) => {
  const f = firebase.functions().httpsCallable("deleteBundledSales");
  const { data } = await f({
    ids: payload.map((item) => item.id),
  });
  return data;
};

export async function deleteSale(id: string) {
  await db.runTransaction(async (t) => {
    const doc = db.collection("Sales").doc(id);
    const sale = await t.get(doc);
    if (!sale.exists)
      throw new Error(`deleteSale: Sale (#${id}) doesn't exist`);

    const data = sale.data();

    if (data?.is_return || data?.original_id) {
      if (data?.original_id) {
        const originalRef = db.collection("Sales").doc(data.original_id);
        try {
          const original = await t.get(originalRef);
          if (original.exists) {
            t.update(originalRef, {
              return_id: firebase.firestore.FieldValue.delete(),
            });
          }
        } catch (e) {
          console.warn(
            `Couldn't delete return_id in #${data.original_id} (deleted return ${id})`,
            e
          );
        }
      }
    } else {
      const transactionInventoryId = data?.transaction_inventory_id;
      if (transactionInventoryId) {
        const transactionDoc = db
          .collection("Transactions")
          .doc(transactionInventoryId);
        try {
          const transaction = await t.get(transactionDoc);
          if (transaction.exists) {
            const transactionData = transaction.data();
            await t.update(transactionDoc, {
              items: (transactionData?.items || []).filter(
                ({ type, id: itemId }) => type !== "sale" || itemId !== id
              ),
            });
          }
        } catch (e) {
          console.warn(
            "[sales] deleteSale: failed to unlink from linked transaction; continuing delete",
            { saleId: id, transactionInventoryId, error: e }
          );
        }
      }
    }

    await t.delete(doc);
  });
}

export async function deleteReturn(returnId: string) {
  if (!returnId) return;
  return await deleteSale(returnId);
}

export async function uploadSalesCsv(payload: File | Blob) {
  const userId = await getUserId();
  const storageRef = storage().ref();
  const basename = File.name + Date.now();
  const filename: string = `sales/${userId}/${basename}`;
  const uploadRef = storageRef.child(filename);
  const snapshot = await uploadRef.put(payload);
  if (snapshot.state === firebase.storage.TaskState.SUCCESS)
    return { basename, filename };

  throw new Error("upload failed");
}

export const consolidateEbaySaleShippingAndAnalyticsCost = async (
  orderIds: Array<string>
) => {
  const f = firebase.functions().httpsCallable("consolidateOrders");
  return (await f({ orderIds })).data;
};

export async function deleteManySales(ids: Sale["id"][]) {
  if (!ids.length) return;

  await parallelize(ids, (id) => deleteSale(id), 10);
}

export async function reviewManySales(
  ids: Sale["id"][],
  unreviewed = false,
  vendors: Record<string, string> = {}
) {
  if (!ids.length) return;

  for (const c of chunk(ids, MaximumBatchSize)) {
    const batch = db.batch();
    for (const id of c) {
      const doc = db.collection("Sales").doc(id);
      const data: { unreviewed: boolean; vendor?: string } = { unreviewed };
      if (vendors[id]) data.vendor = vendors[id];
      batch.update(doc, data);
    }
    await batch.commit();
  }
}

export async function uploadSalesFile(rows, metadata = {}) {
  const f = firebase.functions().httpsCallable("upload-uploadSales");

  return (await f({ rows, metadata })).data;
}

export async function bulkEditSales(rows, metadata = {}) {
  const f = firebase.functions().httpsCallable("upload-bulkEditSales");
  const ret = (await f({ rows, metadata })).data;
  return ret;
}

export async function addBundledSales(sales: Sale[]) {
  if (sales.length === 0) return;
  const userId = await getUserId();
  return await db.runTransaction(async (transaction) => {
    const userData = await transaction.get(db.collection("Users").doc(userId));
    const income_tax_rate = userData.data()?.income_tax_rate || 0;
    const items: Record<
      string,
      {
        ref: firebase.firestore.DocumentReference;
        data: Inventory;
        amount: number;
        totalPurchasePrice: number;
      }
    > = {};

    // Get all inventory information
    for (const sale of sales) {
      // id is from inventory
      const { id: inventoryId, purchase_price } = sale;
      if (!inventoryId) continue;
      if (items[inventoryId]) {
        items[inventoryId].amount = items[inventoryId].amount + 1;
        items[inventoryId].totalPurchasePrice =
          items[inventoryId].totalPurchasePrice + (purchase_price || 0);
        continue;
      }

      const ref = db.collection("Inventory").doc(inventoryId);
      const doc = await transaction.get(ref);
      if (!doc.exists) continue;
      const data = doc.data() as Inventory;
      if (!data) continue;

      items[inventoryId] = {
        ref,
        data,
        amount: 1,
        totalPurchasePrice: purchase_price || 0,
      };
    }

    // Update or delete inventory
    for (const item of Object.values(items)) {
      const { ref, data, amount, totalPurchasePrice } = item;
      const quantity = data?.quantity || 1;

      if (quantity > amount) {
        const purchasePrice = data?.purchase_price || 0;
        const all = totalPurchasePrice
          ? totalPurchasePrice
          : Math.round((amount * purchasePrice * 100) / amount) / 100;
        await transaction.update(ref, {
          quantity: quantity - amount,
          purchase_price: Math.round((purchasePrice - all) * 100) / 100,
        });
      } else {
        await transaction.delete(ref);
      }
    }

    // Create sales
    const ids: string[] = [];
    for (const sale of sales) {
      const doc = db.collection("Sales").doc();
      const data = payloadToSale(userId, doc.id, sale, income_tax_rate);
      await transaction.set(doc, data);
      ids.push(doc.id);
    }

    return ids;
  });
}
